import React, { useRef, useState } from 'react';
import { Download } from 'lucide-react';
import Button from '../../components/ui/button';
import ConfirmationDialog from '../../components/ConfirmationDialog';
import { useToast } from '../../hooks/useToast';
import { AdminExportTable, downloadAdminExport, getAdminExportCount } from './api';

// "Export CSV" button for an admin table: counts the rows, asks for
// confirmation, then downloads every column of the table, newest rows first
// up to the backend's limit. The file comes from the backend, not from the
// rows on screen, so it covers every page and ignores the page's filters.
// Backend: GET /api/admin/export/:table(/count), services/adminCsvExport.js.
// Docs: docs/features/implemented/2025.09.15 - Admin Panel v1.md ("CSV export").

const rowsText = (n: number) => `${n.toLocaleString('en-US')} row${n === 1 ? '' : 's'}`;

// Local date, so the file name matches the admin's calendar.
function todayStamp() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function saveFile(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoked a moment later: some browsers read the URL after click() returns.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// A failed blob request carries its JSON error body as a Blob.
async function errorMessage(e: any): Promise<string> {
  const data = e?.response?.data;
  if (data instanceof Blob) {
    try {
      return JSON.parse(await data.text()).error || 'Export failed.';
    } catch {
      return 'Export failed.';
    }
  }
  return data?.error || e?.message || 'Export failed.';
}

interface AdminCsvExportButtonProps {
  table: AdminExportTable;
  /** What the rows are, for the dialog title: "users", "LLM requests". */
  label: string;
}

const AdminCsvExportButton: React.FC<AdminCsvExportButtonProps> = ({ table, label }) => {
  const { showToast } = useToast();
  const [busy, setBusy] = useState(false);
  const [counts, setCounts] = useState<{ total: number; limit: number } | null>(null);
  // The dialog confirms on Enter AND the focused button clicks on Enter;
  // this keeps that from downloading the file twice.
  const downloading = useRef(false);

  const fail = async (e: unknown) => {
    showToast({ title: 'Export failed', message: await errorMessage(e), type: 'error' });
  };

  const openConfirmation = async () => {
    setBusy(true);
    try {
      const result = await getAdminExportCount(table);
      if (result.total === 0) {
        showToast({ title: 'Nothing to export', message: `There are no ${label} yet.`, type: 'info' });
      } else {
        setCounts(result);
      }
    } catch (e) {
      await fail(e);
    } finally {
      setBusy(false);
    }
  };

  const download = async () => {
    if (downloading.current) return;
    downloading.current = true;
    setCounts(null);
    setBusy(true);
    try {
      saveFile(await downloadAdminExport(table), `jody-${table}-${todayStamp()}.csv`);
    } catch (e) {
      await fail(e);
    } finally {
      downloading.current = false;
      setBusy(false);
    }
  };

  return (
    <>
      <Button variant="secondary" onClick={openConfirmation} disabled={busy}>
        <Download size={14} className="mr-1.5" aria-hidden />
        {busy ? 'Exporting…' : 'Export CSV'}
      </Button>
      {counts && (
        <ConfirmationDialog
          title={`Export ${label} to CSV?`}
          message={
            counts.total > counts.limit
              ? `This table has ${rowsText(counts.total)}. The CSV will have the newest ${counts.limit.toLocaleString('en-US')}.`
              : `The CSV will have ${rowsText(counts.total)}, with every column.`
          }
          confirmText="Download CSV"
          confirmButtonStyle="primary"
          onConfirm={download}
          onCancel={() => setCounts(null)}
        />
      )}
    </>
  );
};

export default AdminCsvExportButton;
